import { useMemo } from 'react';
import { DEFAULT_MAIN_COLUMNS, MAIN_COLUMN_KEYS } from '@/entities/conversations';
import { useExpertReports, useExpertReportsBlocksMap } from '@/entities/expert-report';
import { useAttributesSettings } from '../DialogsListAttributesSettings';
import { ATTRIBUTE_COLUMN_PREFIX, EXPERT_REPORT_STANDARD_COLUMNS } from './constants';
import { useDialogDataGridColumns } from './useDialogDataGridColumns';
import type { ContentSearchColumnOptions, ExpertReportBlocksMap } from './useDialogTableColumns';

/** Параметры построения данных колонок таблицы диалогов */
type UseDialogColumnsDataParams = {
  /** Колонки поиска по содержимому диалога — появляются при активном режиме поиска */
  contentSearch?: ContentSearchColumnOptions;
};

/**
 * Загружает все источники данных для таблицы диалогов и строит колонки DataGrid.
 * Вычисляет дефолтную видимость для всех динамических колонок.
 *
 * Не знает о стейте и персистенции — только о данных и структуре колонок.
 */
export const useDialogColumnsData = ({ contentSearch }: UseDialogColumnsDataParams = {}) => {
  const { attributes: allAttributes, isLoadingAttributes } = useAttributesSettings();

  const { data: reportsData } = useExpertReports({ isDeleted: false, limit: 100 });
  const allReports = useMemo(() => reportsData?.items ?? [], [reportsData?.items]);
  const isReportsLoaded = reportsData !== undefined;

  const allReportIds = useMemo(() => allReports.map((r) => r.id), [allReports]);
  const blocksByReportId = useExpertReportsBlocksMap(allReportIds) as ExpertReportBlocksMap;

  const columns = useDialogDataGridColumns({ allAttributes, allReports, blocksByReportId, contentSearch });

  /**
   * Дефолтная видимость: основные колонки видимы по DEFAULT_MAIN_COLUMNS,
   * остальные динамические колонки скрыты при первом открытии.
   * Включает основные колонки, атрибуты, стандартные колонки отчётов и блоки всех отчётов.
   * Обновляется при подгрузке новых блоков.
   */
  const defaultVisibility = useMemo(() => {
    const vis: Record<string, boolean> = {};

    // Основные колонки: видимы только те, что в DEFAULT_MAIN_COLUMNS
    MAIN_COLUMN_KEYS.forEach((key) => {
      vis[key] = DEFAULT_MAIN_COLUMNS.includes(key);
    });

    allAttributes.forEach((attr) => {
      vis[`${ATTRIBUTE_COLUMN_PREFIX}${attr.path}`] = false;
    });

    allReports.forEach((report) => {
      EXPERT_REPORT_STANDARD_COLUMNS.forEach((col) => {
        vis[`expertReport_${report.id}_${col}`] = false;
      });

      blocksByReportId.get(report.id)?.forEach((_, blockId) => {
        vis[`expertReport_${report.id}_block_${blockId}`] = false;
      });
    });

    return vis;
  }, [allAttributes, allReports, blocksByReportId]);


  return {
    columns,
    defaultVisibility,
    allAttributes,
    allReports,
    blocksByReportId,
    isLoading: isLoadingAttributes || !isReportsLoaded,
  };
};
